import { useEffect } from "react";
import {
  Box,
  Button,
  Center,
  Flex,
  HStack,
  Text,
  chakra,
  useToast,
  useDisclosure,
  Spinner,
} from "@chakra-ui/react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useRecoilState, useResetRecoilState } from "recoil";
import {
  createCampaign,
  getSingleCampaign,
  updateCampaign,
} from "../../API/campaign";
import { createCampaignAtom } from "../../atoms/campaigns";
import CampaignBuilderStage from "../../components/campaigns/NewCampaign/CampaignBuilderStage";
import GettingStarted from "../../components/campaigns/NewCampaign/GettingStarted";
import RecipientsStage from "../../components/campaigns/NewCampaign/RecipientsStage";
import SendScheduleStage from "../../components/campaigns/NewCampaign/SendScheduleStage";
import SettingssStage from "../../components/campaigns/NewCampaign/SettingsStage";
import Templates from "../../components/campaigns/NewCampaign/Templates";
import Success from "../../components/UI/Success";
import { toaster } from "../../utils/toast";
import { PageAnimate } from "../../components/animations/pageAnimate";
import { tipTapAtom } from "../../atoms/tiptap";
import EmailTemplates from "../Account/Templates/VisualEmailTemplates";
import EmailCampaignTemplates from "../../components/campaigns/NewCampaign/EmailCampaignTemplates";

export type campaignStageProps =
  | "Getting Started"
  | "Templates"
  | "Campaign Builder"
  | "Recipients"
  | "Settings"
  | "Send or schedule"
  | "success";
// | {[x: string]: any}

const NewCampaign = () => {
  const [currentStageData, setCurrentStageData] =
    useRecoilState(createCampaignAtom);
  const [tiptapData, setTiptapData] = useRecoilState(tipTapAtom);

  
  const [searchParams] = useSearchParams();

  const resetStageData = useResetRecoilState(createCampaignAtom);
  const resetTiptapData = useResetRecoilState(tipTapAtom);
  // const active = currentStageData?.data?.channel !== 'SMS' && (currentStageData?.currentStage === 'Templates' ? 'Campaign Builder' : currentStageData?.currentStage)
  // const active = currentStageData?.currentStage === 'success' ? 'final' : currentStageData?.currentStage

  useEffect(() => {
    resetStageData();
    resetTiptapData();
  }, [resetStageData, resetTiptapData]);

  // if edit, fetch data
  // const getDraftQuery = 
  

  useQuery(
    ["get_edit_campaign"],
    () => {
      return getSingleCampaign({
        uid: searchParams.get("uid") || "00",
        channel: searchParams.get("channel") || "sms",
      });
    },
    {
      enabled: !!searchParams.get("uid"),
      onSuccess: (res) => {
        console.log("res",res);
        setCurrentStageData((prev) => {
          return {
            ...prev,
            
            data: {
              ...prev?.data,
              // campaign_name: res.campaign_name,
              // list_uid: res.list_uid,
              // sms_sender_uid: res.sms_sender_uid,
              // timezone: res.timezone,
              // send_date: res.send_date,
              // message_body: res.message_body,

              ...res,
              channel:
                res.channel === "SMS" || res.channel === "sms"
                  ? "SMS"
                  : res.channel === "email"
                  ? "Email"
                  : res.channel,
                  
            },  
          };
        });

        setCurrentStageData((prev) => {
          return {
            ...prev,
            currentStage:                
                    res.campaign_name && res.channel && res.message_body && res.no_of_contacts > 0
                    ? "Settings"
                    :res.campaign_name && res.channel && res.message_body
                    ? "Campaign Builder"
                    : res.campaign_name && res.channel
                    ? "Templates"
                    : "Getting Started"
                    
                    ,
          }
        });
        

        setTiptapData((prev) => {
          return {
            ...prev,
            linkObject: {
              ...prev.linkObject,
              link_uid: res.link_uid,
            },
            content: res.message_body,
          };
        });
      },
    }
  );

  const stages: campaignStageProps[] = [
    "Getting Started",
    "Templates",
    "Campaign Builder",
    "Recipients",
    "Settings",
    "Send or schedule",
  ];

  const filteredStages =
    currentStageData?.data?.channel !== "SMS"
      ? stages.filter((item) => item !== "Templates")
      : stages;

  // const resetData = useResetRecoilState(createCampaignAtom);

  const navigate = useNavigate();
  const toast = useToast();
  const queryClient = useQueryClient();

  // Create campaign
  const { mutate: createCampaignHandler, isLoading: createCampaignLoading } =
    useMutation(createCampaign, {
      onSuccess(data) {
        navigate("/campaigns");
        const success = { ...data };
        queryClient.invalidateQueries(["allCampaignsData"]);
        toaster(toast, success);
      },
      

      onError(data: any) {
        const errors = { ...data };
        toaster(toast, errors);
      },
    });

  // Update existing campaign
  const { mutate: updateCampaignHandler, isLoading: updateCampaignLoading } =
    useMutation(updateCampaign, {
      onSuccess(data) {
        const success = { ...data };
        queryClient.invalidateQueries(["allCampaignsData"]);
        navigate("/campaigns");
        toaster(toast, success);
        
      },

      onError(data: any) {
        const errors = { ...data };
        toaster(toast, errors);
      },
    });

  const handleSaveExit = () => {

    if (searchParams?.get("uid")) {
      updateCampaignHandler({
        ...currentStageData?.data,
        campaign_uid: searchParams?.get("uid"),
        save_and_exit: true,
      });
    } else {
      createCampaignHandler({
        ...currentStageData?.data,
        fallbacks: tiptapData.fallbacks,
        save_and_exit: true,
      });
    }
  };

  const sendEmailDisclosure = useDisclosure();

  return (
    <PageAnimate>
      <Box
        bg="white"
        rounded="4px"
        mt="8px"
        w="full"
        minHeight={"calc(100vh - 98px)"}
      >
        {/* header */}
        <Flex
          w="full"
          px={{ base: "24px", md: "40px" }}
          alignItems={"center"}
          borderBottomWidth="1px"
          borderColor={"#eee"}
        >
          <HStack ml="auto" spacing={"25px"}>
            {/* stage number */}
            {stages?.map((stage, index) => {
              const isActive = stage === currentStageData?.currentStage;

              return (
                <Center
                  key={index}
                  flexDir={"column"}
                  textAlign="center"
                  borderBottomWidth={isActive ? "2px" : "1px"}
                  borderColor={isActive ? "primary.500" : "transparent"}
                  pt="20px"
                  pb="10px"
                  mb={"-3px"}
                  px="32px"
                  cursor={"pointer"}
                  onClick={() => {
                   // eslint-disable-next-line no-mixed-operators
                   if (currentStageData.data.builder && currentStageData.data.channel || currentStageData.data.channel=== "SMS"){
                    setCurrentStageData((prevData) => {
                      return {
                        ...prevData,
                        currentStage: stage,
                      };
                    });
                   }
                    
                  }}
                >
                  <Center
                    w="26px"
                    height={"26px"}
                    rounded="full"
                    bg={isActive ? "primary.500" : "white"}
                    color={isActive ? "white" : "grays.12"}
                    fontWeight={500}
                    fontSize="xs"
                    borderWidth={"1px"}
                    borderColor="borders.100"
                  >
                    <Text>{index + 1} </Text>
                  </Center>

                  <Text
                    fontWeight={500}
                    fontSize="10px"
                    mt="8px"
                    color={isActive ? "primary.500" : "grays.12"}
                  >
                    {stage}
                  </Text>
                </Center>
              );
            })}
          </HStack>

          {currentStageData?.currentStage !== "success" && currentStageData?.data.campaign_name && currentStageData?.data.channel ? (
            <chakra.button
              ml="auto"
              rounded="0px"
              borderBottom="2px"
              borderColor={"primary.500"}
              color="primary.500"
              fontSize={"xs"}
              fontWeight={700}
              onClick={handleSaveExit}
              disabled={createCampaignLoading || updateCampaignLoading}
            >
              {createCampaignLoading || updateCampaignLoading ? (
                <Spinner size="sm" />
              ) : (
                "Save & Exit"
              )}
            </chakra.button>
          ) : (
            <chakra.button
              ml="auto"
              rounded="0px"
              borderBottom="2px"
              borderColor={"primary.500"}
              color="primary.500"
              fontSize={"sm"}
              fontWeight={700}
              onClick={() => {
                // resetData();
                navigate("/campaigns");
              }}
            >
              Close
            </chakra.button>
          )}
        </Flex>

        {/* data */}
        <Flex pt={"32px"} pb="64px" minWidth={"100%"}>
          {currentStageData?.currentStage === "Getting Started" && (
            <PageAnimate>
              <GettingStarted />
            </PageAnimate>
          )}
          {currentStageData?.currentStage === "Templates" &&
            currentStageData?.data?.channel === "SMS" && (
              <PageAnimate>
                <Templates />
              </PageAnimate>
            )}
            {currentStageData?.currentStage === "Templates" &&
            currentStageData?.data?.channel === "Email" && (
              <PageAnimate>
                <EmailCampaignTemplates />
              </PageAnimate>
            )}
          {currentStageData?.currentStage === "Campaign Builder" && (
            <PageAnimate>
              <CampaignBuilderStage disclosure={sendEmailDisclosure} />
            </PageAnimate>
          )}
          {currentStageData?.currentStage === "Recipients" && (
            <PageAnimate>
              <RecipientsStage />
            </PageAnimate>
          )}
          {currentStageData?.currentStage === "Settings" && (
            <PageAnimate>
              <SettingssStage />
            </PageAnimate>
          )}
          {currentStageData?.currentStage === "Send or schedule" && (
            <PageAnimate>
              <SendScheduleStage />
            </PageAnimate>
          )}
          {currentStageData?.currentStage === "success" && (
            <PageAnimate>
              <Flex
                flexDir={"column"}
                justifyContent="center"
                alignItems={"center"}
                w="full"
                h="full"
              >
                <Success
                  title="Your campaign has been
successfully sent"
                  containerProps={{
                    maxW: "334px",
                  }}
                  icon={
                    <svg
                      width="80"
                      height="80"
                      viewBox="0 0 80 80"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        opacity="0.12"
                        d="M34.9669 1.76956C37.7258 -0.589853 42.2441 -0.589853 45.043 1.76956L51.3607 7.20814C52.5603 8.24788 54.7994 9.0878 56.3988 9.0878H63.1963C67.4347 9.0878 70.9133 12.5669 70.9133 16.8058V23.6039C70.9133 25.1636 71.753 27.443 72.7926 28.6427L78.2307 34.9612C80.5898 37.7205 80.5898 42.2395 78.2307 45.0388L72.7926 51.357C71.753 52.5567 70.9133 54.7962 70.9133 56.3958V63.1942C70.9133 67.4331 67.4347 70.9122 63.1963 70.9122H56.3988C54.8394 70.9122 52.5603 71.7521 51.3607 72.7919L45.043 78.2304C42.2841 80.5899 37.7658 80.5899 34.9669 78.2304L28.6493 72.7919C27.4497 71.7521 25.2105 70.9122 23.6111 70.9122H16.6937C12.4553 70.9122 8.97662 67.4331 8.97662 63.1942V56.3558C8.97662 54.7962 8.13697 52.5567 7.13735 51.357L1.73935 44.9988C-0.579782 42.2394 -0.579782 37.7606 1.73935 35.0012L7.13735 28.6427C8.13697 27.443 8.97662 25.2036 8.97662 23.644V16.8058C8.97662 12.5669 12.4553 9.0878 16.6937 9.0878H23.6111C25.1706 9.0878 27.4497 8.24788 28.6493 7.20814L34.9669 1.76956Z"
                        fill="#159D6F"
                      />
                      <path
                        fillRule="evenodd"
                        clipRule="evenodd"
                        d="M45.4666 31.1801C44.7022 32.6114 44.166 34.5956 44.166 36.6719C44.166 38.7481 44.7022 40.7323 45.4666 42.1636C45.8489 42.8795 46.2643 43.4122 46.6538 43.7507C46.8276 43.9016 46.9806 44.0013 47.1114 44.0656L47.1399 44.0715L47.591 44.1645C47.7614 44.1385 48.0161 44.0364 48.3449 43.7507C48.7344 43.4122 49.1498 42.8795 49.5321 42.1636C50.2965 40.7323 50.8327 38.7481 50.8327 36.6719C50.8327 34.5956 50.2965 32.6114 49.5321 31.1801C49.1498 30.4642 48.7344 29.9315 48.3449 29.593C48.0161 29.3074 47.7614 29.2053 47.591 29.1792L47.1399 29.2722L47.1114 29.2781C46.9806 29.3424 46.8276 29.4421 46.6538 29.593C46.2643 29.9315 45.8489 30.4642 45.4666 31.1801ZM41.666 36.6719C41.666 39.1072 42.2779 41.4636 43.2186 43.2605C42.3495 43.0803 41.4127 42.8857 40.4626 42.6877C36.6927 41.9023 32.7712 41.0764 31.9692 40.8759C31.4749 40.7523 30.7608 40.3047 30.1577 39.5188C29.5714 38.7548 29.166 37.7665 29.166 36.6719C29.166 35.5772 29.5714 34.5889 30.1577 33.825C30.7608 33.0391 31.4748 32.5915 31.9692 32.4679C32.7712 32.2674 36.6927 31.4414 40.4626 30.656C41.4127 30.4581 42.3495 30.2635 43.2186 30.0832C42.2779 31.8801 41.666 34.2366 41.666 36.6719ZM45.0067 46.1838C45.5451 46.2951 46.0095 46.3909 46.3736 46.4661C46.7269 46.5962 47.1035 46.6719 47.4993 46.6719C48.4608 46.6719 49.3085 46.2253 49.9846 45.6378C50.6653 45.0464 51.2551 44.2443 51.7374 43.3413C52.7022 41.5346 53.3327 39.1438 53.3327 36.6719C53.3327 34.1999 52.7022 31.8091 51.7374 30.0025C51.2551 29.0994 50.6653 28.2974 49.9846 27.7059C49.3085 27.1185 48.4608 26.6719 47.4993 26.6719C47.1035 26.6719 46.7269 26.7476 46.3736 26.8777C46.0095 26.9528 45.5451 27.0487 45.0067 27.16C43.6522 27.44 41.8286 27.8178 39.9527 28.2086C36.2227 28.9857 32.2275 29.8264 31.3628 30.0425C30.1905 30.3356 29.0296 31.1886 28.1744 32.3029C27.3023 33.4393 26.666 34.951 26.666 36.6719C26.666 38.3927 27.3023 39.9044 28.1744 41.0408C28.6925 41.7159 29.3229 42.2951 30 42.712V48.75C30 51.2814 32.052 53.3333 34.5833 53.3333H35.4167C37.948 53.3333 40 51.2814 40 48.75V45.145C41.8592 45.5323 43.6635 45.9061 45.0067 46.1838ZM37.5 44.6228C35.5188 44.2076 33.676 43.8175 32.5 43.5598V48.75C32.5 49.9006 33.4327 50.8333 34.5833 50.8333H35.4167C36.5673 50.8333 37.5 49.9006 37.5 48.75V44.6228Z"
                        fill="#159D6F"
                      />
                    </svg>
                  }
                />

                <Link
                  to={`/campaigns/${currentStageData.data.channel}/${currentStageData?.newCampaignId}/reports`}
                >
                  <Button
                    w="full"
                    mx="auto"
                    mt="-48px"
                    colorScheme={"primary"}
                    // onClick={resetData}
                  >
                    View reports
                  </Button>
                </Link>
              </Flex>
            </PageAnimate>
          )}
        </Flex>
      </Box>
    </PageAnimate>
  );
};

export default NewCampaign;
